import { useMemo, useRef } from "react";
import type { CalEvent, WorkingHours } from "../types";
import { displaySubject, type EventVisual } from "../domain/day";
import { addDays, dayKeyOf, dayLabel, dayStart, fd, hm, minutesOfDay } from "../domain/time";
import { weekModel } from "../domain/week";
import { WarnIcon } from "./CalendarIcons";
import { CalendarLoadError } from "./CalendarLoadError";
import styles from "./CalendarModule.module.css";

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

const BAR_CLASS: Record<EventVisual, string> = {
  declined: styles.weekBarDec!,
  focus: styles.weekBarFocus!,
  conflict: styles.weekBarConf!,
  draft: styles.weekBarDraft!,
  invite: styles.weekBarInv!,
  tentative: styles.weekBarTent!,
  default: "",
};

/** Bars shorter than this share of the column are drawn at this height, so they stay visible. */
const MIN_BAR_PERCENT = 2;

interface WeekViewProps {
  readonly mon: string;
  readonly today: string;
  readonly selectedDay: string;
  readonly nowMin: number;
  readonly events: readonly CalEvent[];
  readonly workingHours: WorkingHours;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly acks: Readonly<Record<string, string>>;
  readonly onOpenDay: (day: string) => void;
  readonly onOpenEvent: (eventId: string) => void;
  /** Whether the token allows replying from AiVA; without it, replies are made in Outlook. */
  readonly writable: boolean;
  /** Accept, Tentative or Decline an invitation (sent when the Undo window ends). */
  readonly onRespond: (event: CalEvent, response: "yes" | "may" | "no") => void;
}

/** "7.5h" — the mockup's one-decimal hours. */
const hoursLabel = (min: number) => `${Math.round(min / 6) / 10}h`;

/** The week at a glance: load per day, clashes, and invitations awaiting your reply. */
export function WeekView({
  mon,
  today,
  selectedDay,
  nowMin,
  events,
  workingHours,
  loading,
  error,
  onRetry,
  acks,
  onOpenDay,
  onOpenEvent,
  writable,
  onRespond,
}: Readonly<WeekViewProps>) {
  const model = useMemo(() => weekModel(events, mon, acks, workingHours), [events, mon, acks, workingHours]);
  const awaitingRef = useRef<HTMLDivElement>(null);

  if (error) return <CalendarLoadError message={error} onRetry={onRetry} />;

  if (loading) return <div className={styles.empty}>Loading your calendar…</div>;

  const { stats, range } = model;
  const span = range.endMin - range.startMin;
  const toPercent = (min: number) => ((min - range.startMin) / span) * 100;
  const firstClashDay = model.firstClashDay;

  return (
    <>
      <div className={styles.stats}>
        <div className={styles.stat}>
          <div className={styles.statValue}>{stats.meetings}</div>
          <div className={styles.statLabel}>Meetings</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statValue}>{hoursLabel(stats.bookedMin)}</div>
          <div className={styles.statLabel}>Booked</div>
        </div>
        {firstClashDay ? (
          <button
            type="button"
            className={`${styles.stat} ${styles.statErr}`}
            onClick={() => onOpenDay(firstClashDay)}
            title="Open the first clash"
          >
            <div className={styles.statValue}>{stats.clashes}</div>
            <div className={styles.statLabel}>Clashes</div>
          </button>
        ) : (
          <div className={styles.stat}>
            <div className={styles.statValue}>0</div>
            <div className={styles.statLabel}>Clashes</div>
          </div>
        )}
        <button
          type="button"
          className={`${styles.stat} ${stats.toReply ? styles.statNotice : ""}`}
          onClick={() => awaitingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
          title="Show invitations awaiting your reply"
        >
          <div className={styles.statValue}>{stats.toReply}</div>
          <div className={styles.statLabel}>To Reply</div>
        </button>
      </div>

      <div className={styles.sec}>
        <div className={styles.secH}>
          <span className={styles.secBar} />
          <span className={styles.secTitle}>This Week</span>
          <span className={`${styles.secRight} ${styles.secMeta}`}>Select a day to open it</span>
        </div>
        <div className={styles.week}>
          {model.days.map((d) => {
            const date = dayStart(d.day);
            const isToday = d.day === today;
            const dayName = DAY_SHORT[date.getDay()] ?? "";
            const load = d.loadMin ? fd(d.loadMin) : "Clear";
            const clashWord = d.clashCount === 1 ? "clash" : "clashes";
            const clashes = d.clashCount ? `, ${d.clashCount} ${clashWord}` : "";
            return (
              <button
                key={d.day}
                type="button"
                className={`${styles.weekCol} ${d.day === selectedDay ? styles.weekColOn : ""} ${isToday ? styles.weekColToday : ""}`}
                onClick={() => onOpenDay(d.day)}
                aria-label={`${dayLabel(d.day)}, ${load}${clashes}`}
              >
                <span className={styles.weekDow}>{dayName}</span>
                <span className={styles.weekNum}>{date.getDate()}</span>
                <span className={styles.weekBar}>
                  {d.bars.map((b) => {
                    const top = Math.max(0, toPercent(b.startMin));
                    const height = Math.max(MIN_BAR_PERCENT, toPercent(Math.min(b.endMin, range.endMin)) - top);
                    return (
                      <i
                        key={b.event.id}
                        className={`${styles.weekBarItem} ${BAR_CLASS[b.visual]}`}
                        style={{ top: `${top}%`, height: `${height}%` }}
                        title={`${hm(b.startMin)} ${displaySubject(b.event)}`}
                      />
                    );
                  })}
                  {isToday && nowMin >= range.startMin && nowMin <= range.endMin && (
                    <i className={styles.weekNow} style={{ top: `${toPercent(nowMin)}%` }} />
                  )}
                </span>
                <span className={styles.weekLoad}>{load}</span>
              </button>
            );
          })}
        </div>
        <div className={styles.legend}>
          <span>
            <i className={styles.legendBooked} />Booked
          </span>
          <span>
            <i className={styles.legendClash} />Clash
          </span>
          <span>
            <i className={styles.legendReply} />Needs reply
          </span>
          <span>
            <i className={styles.legendTentative} />Tentative
          </span>
        </div>
        {model.weekendCount > 0 && (
          <button type="button" className={styles.newLink} onClick={() => onOpenDay(addDays(mon, 5))}>
            Weekend · {model.weekendCount} meeting{model.weekendCount === 1 ? "" : "s"}
          </button>
        )}
      </div>

      <div className={styles.sec} ref={awaitingRef}>
        <div className={styles.secH}>
          <span className={`${styles.secBar} ${styles.secBarNotice}`} />
          <span className={`${styles.secTitle} ${styles.secTitleNotice}`}>Awaiting Your Reply</span>
          <span className={`${styles.secRight} ${styles.secMeta}`}>{stats.toReply}</span>
        </div>
        {model.awaiting.length === 0 ? (
          <div className={`${styles.card} ${styles.caughtUp}`}>You are all caught up</div>
        ) : (
          model.awaiting.map(({ event, clashes }) => (
            <div key={event.id} className={styles.card}>
              <button type="button" className={styles.cardTitle} onClick={() => onOpenEvent(event.id)}>
                {displaySubject(event)}
              </button>
              <div className={styles.cardMeta}>
                {dayLabel(dayKeyOf(event.start))} · {hm(minutesOfDay(event.start))}–{hm(minutesOfDay(event.end))} · from{" "}
                {event.organizerName}
              </div>
              {clashes ? (
                <div className={styles.cardClash}>
                  <WarnIcon />
                  Clashes with something you have already accepted
                </div>
              ) : (
                <div className={styles.cardFree}>You are free at this time</div>
              )}
              {writable ? (
                <div className={styles.cardActions}>
                  <button type="button" className={styles.btn} onClick={() => onRespond(event, "yes")}>
                    Accept
                  </button>
                  <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => onRespond(event, "may")}>
                    Tentative
                  </button>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnSecondary} ${styles.btnDanger}`}
                    onClick={() => onRespond(event, "no")}
                  >
                    Decline
                  </button>
                </div>
              ) : (
                <div className={styles.hint}>Reply from Outlook — AiVA can't change your calendar with your current permissions.</div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
}
